import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, MessageCircle, LifeBuoy, Shield, CalendarRange, Kanban, MailCheck, Users, FileBarChart, Compass, BadgeCheck, UserRound, Building, Activity } from 'lucide-react';
import { Drawer, useToast } from '@kollez/design-system';
import { useAuthStore } from '@/lib/authStore';

/** /help — direct port of kollez/pages/shared/help.html. */
const TOPICS_TPO: Array<[typeof CalendarRange, string, string]> = [
  [CalendarRange, 'Running drives', 'Posting, eligibility, tiers'],
  [Kanban, 'Screening', 'Shortlisting, bulk actions, shortcuts'],
  [MailCheck, 'Offers', 'Sign-off checks and release'],
  [Users, 'Students', 'Importing and fixing the roster'],
  [FileBarChart, 'Reports', 'NIRF and NAAC exports'],
];

const TOPICS_STUDENT: Array<[typeof CalendarRange, string, string]> = [
  [UserRound, 'Your profile', 'Fix academics, resumes, skills'],
  [Compass, 'Applying', 'Eligibility, withdrawing, deadlines'],
  [BadgeCheck, 'Offers', 'Accepting, declining, switching'],
  [Shield, 'Privacy', 'Who sees your data'],
];

const TOPICS_PLATFORM: Array<[typeof CalendarRange, string, string]> = [
  [Building, 'Tenants', 'Provisioning and caps'],
  [Activity, 'Incidents', 'Runbooks and status page'],
];

const FAQS_TPO: Array<[string, string]> = [
  ['Why can\'t some students apply to a drive?', 'Each drive checks branch, CGPA, 10th/12th, backlogs and your placement policy. Open the student\'s record to see which rule stopped them.'],
  ['How do Day 0, Day 1 and Day 2 work?', 'Tiers are set from CTC (Day 0 is ₹9 LPA and above by default). A student placed in a tier can only apply to higher tiers. Your group admin sets the thresholds.'],
  ['Can I undo a bulk shortlist?', 'Yes. Every move shows an Undo for 8 seconds, and students aren\'t told until you publish results.'],
  ['What does offer sign-off check?', 'Name against the roster, CTC against the drive, placement policy, and the signatory. You only need to read what failed.'],
  ['How do I export for NIRF?', 'Your group admin runs the group export from Reports. You can export your own college from Reports → NIRF.'],
];

const FAQS_STUDENT: Array<[string, string]> = [
  ['Can I apply to more drives after accepting an offer?', "Only to Day 0 drives (usually ₹9 LPA and above). Your college's placement policy decides this; you'll see it on each drive before you apply."],
  ['My CGPA is wrong. How do I fix it?', "Academics are verified by your placement office, so you can't edit them yourself. Open Profile → Academics → \"Ask the placement office to correct it\"."],
  ['Who can see my phone number?', 'Your placement office always. A company sees it only after they shortlist you.'],
  ['How do I delete my account?', 'Settings → Your data → Delete account. Your college keeps your placement outcome for NIRF, without your contact details.'],
];

export function HelpPage() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [q, setQ] = useState('');
  const [ticketOpen, setTicketOpen] = useState(false);

  const role = user?.role ?? 'tpo';
  const isStudent = role === 'student' || role === 'student_representative';
  const isPlatform = role?.startsWith('platform_');
  const topics = isStudent ? TOPICS_STUDENT : isPlatform ? TOPICS_PLATFORM : TOPICS_TPO;
  const faqs = isStudent ? FAQS_STUDENT : FAQS_TPO;

  const shown = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return faqs.filter((f) => !ql || (f[0] + f[1]).toLowerCase().includes(ql));
  }, [q, faqs]);

  const firstName = user?.fullName?.split(' ')[0] ?? 'there';

  return (
    <div className="stack">
      <section className="card inkwell" style={{ padding: 28, display: 'grid', gap: 14 }}>
        <h1 className="h-serif m-0" style={{ fontSize: '2rem', lineHeight: 'tight' }}>How can we help, {firstName}?</h1>
        <div className="input-group" style={{ height: 48 }}>
          <Search size={16} />
          <input className="input" style={{ height: 48, fontSize: '1rem' }} placeholder="Search help — e.g. release offers, import roster" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <p className="m-0 text-sm" style={{ opacity: 0.8 }}>Or <Link to="/nexie" style={{ color: 'inherit', textDecoration: 'underline' }}>ask Nexie</Link> — it knows your drives and settings.</p>
      </section>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {topics.map(([Icon, title, sub]) => (
          <Link key={title} to="#" className="card p-4 flex gap-3 items-start">
            <span className="logo-tile"><Icon size={14} /></span>
            <div>
              <div className="cell-title">{title}</div>
              <div className="cell-sub">{sub}</div>
            </div>
          </Link>
        ))}
      </div>

      <section className="card">
        <div className="card-head">
          <h2 className="h-section">Common questions</h2>
          <span className="helper">{q ? `${shown.length} match${shown.length === 1 ? '' : 'es'}` : ''}</span>
        </div>
        <div>
          {shown.length === 0 ? (
            <div className="empty-state p-6 text-center">
              <h3>No answers for "{q}"</h3>
              <p>Try other words, ask Nexie, or contact support below.</p>
            </div>
          ) : (
            shown.map((f) => (
              <details key={f[0]} className="faq" style={{ borderBottom: '1px solid rgb(var(--line-tertiary))' }}>
                <summary style={{ listStyle: 'none', cursor: 'pointer', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', gap: 12, fontWeight: 550, fontSize: '.9375rem' }}>
                  {f[0]}<ChevronDown size={14} />
                </summary>
                <p style={{ margin: 0, padding: '0 20px 16px', color: 'rgb(var(--ink-secondary))', fontSize: '.9375rem', maxWidth: '70ch' }}>{f[1]}</p>
              </details>
            ))
          )}
        </div>
      </section>

      <section className="card card-pad grid gap-4 sm:grid-cols-[1fr_auto] items-center">
        <div>
          <h2 className="h-section">Still stuck?</h2>
          <p className="helper m-0">Our team in Hyderabad replies within 2 working hours, Mon–Sat, 9 am – 7 pm IST.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a className="btn btn-outline" href="https://wa.me/919999999999" target="_blank" rel="noopener noreferrer"><MessageCircle size={14} />WhatsApp us</a>
          <button className="btn btn-primary" onClick={() => setTicketOpen(true)}><LifeBuoy size={14} />Contact support</button>
        </div>
      </section>

      <Drawer open={ticketOpen} onClose={() => setTicketOpen(false)} title="Contact support" size="md">
        <div className="grid gap-4">
          <label className="grid gap-1.5">
            <span className="label">What's it about?</span>
            <select className="select">{topics.map(([, t]) => <option key={t}>{t}</option>)}<option>Something else</option></select>
          </label>
          <label className="grid gap-1.5">
            <span className="label">What happened?</span>
            <textarea className="textarea" rows={5} placeholder="What you tried, what you expected, what you saw" />
          </label>
          <label className="flex gap-2 items-center text-sm">
            <input type="checkbox" className="check" defaultChecked /> Attach the page I was on and my browser details
          </label>
          <div className="notice notice-info">
            <Shield size={14} />
            <div>Support may view your workspace to fix this. You'll be emailed when they do.</div>
          </div>
        </div>
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setTicketOpen(false)}>Cancel</button>
          <button className="btn btn-primary ml-auto" onClick={() => {
            setTicketOpen(false);
            toast.show({ message: "Ticket #4852 created. We'll reply on email and WhatsApp." });
          }}>Send</button>
        </div>
      </Drawer>
    </div>
  );
}
